import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useVehicle } from '../context/VehicleContext';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Button, Field, Modal, Checkbox, Badge, ErrorNote, Note,
  inputClass, selectClass, selectClassSm,
} from '../components/ui/Iparc';
import { signal } from '../theme/iparc';
import { downloadLedgerCSV } from '../utils/exportLedgerCSV';
import TransactionHistoryModal from '../components/finance/TransactionHistoryModal';

// A line whose updatedAt has moved past its createdAt has been changed since it
// was written. Derived on the client so the ledger needs no extra request to
// know which rows have a history worth opening — the log itself is only fetched
// when someone asks for it.
//
// 1s of slack: mongoose sets both timestamps on insert and they can differ by a
// millisecond, which would flag every row as modified.
const wasModified = (entry) => {
  if (!entry?.createdAt || !entry?.updatedAt) return false;
  return new Date(entry.updatedAt) - new Date(entry.createdAt) > 1000;
};

// Compact controls for the inline "add a line" row.
const cell = `${inputClass} h-[30px] px-2 text-[12px] rounded-[7px]`;
const cellSelect = selectClassSm;

// One figure in the summary bar. Hairline dividers, not six coloured cards.
const Stat = ({ label, children, tone, title }) => (
  <div className="min-w-0 px-[18px] py-4 flex-1" title={title}>
    <p className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] mb-1.5 truncate">{label}</p>
    <div
      className="font-['Space_Grotesk',sans-serif] tabular-nums text-[19px] font-semibold leading-none"
      style={{ color: tone || '#F4F1EA' }}
    >
      {children}
    </div>
  </div>
);

// A transaction counts toward real cash only once settled. Pending = a
// receivable (type 'in') or payable (type 'out') not yet realized. Old rows
// have no settlement field → treated as settled for backward compatibility.
const isPending = (e) => e.settlement === 'pending';
const isSettled = (e) => e.settlement !== 'pending';

// Today's date (YYYY-MM-DD) for auto-filling the entry date. Côte d'Ivoire is
// UTC+0, so the UTC slice matches the local day.
const todayStr = () => new Date().toISOString().slice(0, 10);

// Same UTC+0 assumption as todayStr: comparing the ISO day slice as a string
// works because YYYY-MM-DD sorts lexicographically the same way it sorts
// chronologically. Both ends are INCLUSIVE — picking 01/08 to 31/08 has to
// include everything booked on the 31st, not stop at its midnight.
const dayOf = (d) => {
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
};

const ymd = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// Quick ranges. `monthsBack: 0` is the current month, 1 the previous one.
// Day 0 of the next month is the last day of this one, which sidesteps
// leap years and 30/31-day months.
const monthRange = (monthsBack) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  return {
    from: ymd(first.getFullYear(), first.getMonth(), 1),
    to: ymd(last.getFullYear(), last.getMonth(), last.getDate()),
  };
};

const monthLabel = (monthsBack) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  return d.toLocaleDateString('fr-FR', { month: 'long' });
};

// Normalize a plate for loose matching (case/spacing/dashes insensitive).
const normPlate = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// An entry belongs to a vehicle if it's attached by id OR the vehicle's plate
// was typed into the reference/note — so loosely-tagged entries still surface.
const entryMatchesVehicle = (e, v) => {
  if (!v) return false;
  if (e.vehicleId && String(e.vehicleId) === String(v._id)) return true;
  const plate = normPlate(v.plateNumber);
  if (!plate) return false;
  return normPlate(`${e.reference || ''} ${e.reason || ''}`).includes(plate);
};

// "2026-08" -> "août 2026". Distinct from monthLabel above, which takes a
// number of months back for the period presets. Built from an explicit UTC
// date so the month never slips a boundary in a negative-offset timezone.
const monthKeyLabel = (key) => {
  const [y, m] = String(key).split('-').map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

// Split a money value into the number and the "F CFA" suffix so the currency
// can render in a smaller font.
const moneyParts = (amount) => {
  const parts = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' })
    .formatToParts(amount || 0);
  let number = '', currency = '';
  parts.forEach((p) => { if (p.type === 'currency') currency += p.value; else number += p.value; });
  return { number: number.trim(), currency: currency.trim() };
};

// Amount on a SINGLE line with a small currency suffix.
// `fit` (default) auto-scales the text down to fit a fixed-width card — whatever
// the amount, it stays on one line, never wraps and never clips. Pass fit=false
// in content-sized contexts (table cells / flex rows) where there's no fixed
// width to fit into and the table already scrolls horizontally.
// `sign` prefixes +/- for the category breakdown.
const Money = ({ value, className = '', sign = '', fit = true }) => {
  const { number, currency } = moneyParts(value);
  const boxRef = React.useRef(null);
  const textRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);

  React.useLayoutEffect(() => {
    if (!fit) return;
    const doFit = () => {
      const box = boxRef.current, text = textRef.current;
      if (!box || !text) return;
      const avail = box.clientWidth;
      const needed = text.scrollWidth;
      setScale(needed > avail && avail > 0 ? Math.max(avail / needed, 0.35) : 1);
    };
    doFit();
    let ro;
    if (typeof ResizeObserver !== 'undefined' && boxRef.current) {
      ro = new ResizeObserver(doFit);
      ro.observe(boxRef.current);
    }
    return () => ro && ro.disconnect();
  }, [number, currency, sign, fit]);

  const inner = (
    <>
      {sign}{number}
      <span className="text-[0.62em] font-semibold opacity-60 ml-0.5">{currency}</span>
    </>
  );

  if (!fit) {
    return <span className={`whitespace-nowrap ${className}`}>{inner}</span>;
  }

  return (
    <span ref={boxRef} className={`block overflow-hidden ${className}`}>
      <span
        ref={textRef}
        className="inline-block whitespace-nowrap origin-left"
        style={{ transform: scale < 1 ? `scale(${scale})` : undefined }}
      >
        {inner}
      </span>
    </span>
  );
};

const CashbookLedger = () => {
  const { token, user } = useAuth();
  const { vehicles, autoSelectVehicle } = useVehicle();

  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [textQuery, setTextQuery] = useState(''); // free-text search over reference/note/category
  // Accounting period. Empty strings = no bound on that end, so you can ask for
  // "everything up to 31/08" without inventing a start date.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const vehicleSearchRef = useRef(null);
  const [form, setForm] = useState({
    date: todayStr(), // auto-filled with today; still editable for back-dating
    category: '',
    vehicleId: '',
    in: '',
    out: '',
    reference: '',
    pending: false // true = receivable/payable (à crédit, pas encore encaissé/payé)
  });
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryIsSupplier, setNewCategoryIsSupplier] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Partial-settle modal: the pending entry being settled + the amount input.
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleAmount, setSettleAmount] = useState('');
  // Correction modal: the entry being edited + a working copy of its fields.
  const [editTarget, setEditTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  // Supplier accounts
  const [selectedSupplier, setSelectedSupplier] = useState(''); // category name
  const [supplierPayOpen, setSupplierPayOpen] = useState(false);
  const [supplierPayAmount, setSupplierPayAmount] = useState('');

  // Totals per group. Computed server-side because it spans every entry, not
  // just the page's loaded set, and re-fetched when the period changes.
  const [groupRows, setGroupRows] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [openGroup, setOpenGroup] = useState(null);

  useEffect(() => {
    if (!token || !user?._id) return;
    fetchFinance();
    fetchVehicles();
    fetchCategories();
  }, [token, user]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setGroupsLoading(true);
    axios.get(`${API_URL}/finance/group-summary`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { start: dateFrom || undefined, end: dateTo || undefined },
    })
      .then((res) => { if (alive) setGroupRows(res.data?.groups || []); })
      .catch((err) => {
        console.error('Group summary error:', err);
        if (alive) setGroupRows([]);
      })
      .finally(() => { if (alive) setGroupsLoading(false); });
    return () => { alive = false; };
  }, [token, dateFrom, dateTo]);

  const fetchFinance = async () => {
    try {
      const res = await axios.get(`${API_URL}/finance/${user._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(res.data);
    } catch (err) {
      console.error('Finance fetch error:', err);
      setError('Erreur chargement des transactions');
    }
  };

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API_URL}/vehicles/assigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      autoSelectVehicle(res.data);
    } catch (err) {
      console.error('Vehicle fetch error:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/finance/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data);
    } catch (err) {
      console.error('Category fetch error:', err);
    }
  };

  // Close vehicle search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (vehicleSearchRef.current && !vehicleSearchRef.current.contains(e.target)) {
        setVehicleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Vehicle search results
  const vehicleSearchResults = useMemo(() => {
    if (!vehicleSearch.trim()) return [];
    const q = vehicleSearch.toLowerCase();
    return vehicles.filter(v =>
      (v.plateNumber || '').toLowerCase().includes(q) ||
      (v.brand || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q) ||
      (v.owner?.name || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [vehicleSearch, vehicles]);

  // The accounting period, applied before every other filter. Everything the
  // page reports — the summary bar, the category breakdown, the journal —
  // reads from this rather than from `entries`, so a period's figures can
  // never disagree with the rows shown underneath them.
  const periodActive = !!(dateFrom || dateTo);
  const rangedEntries = useMemo(() => {
    if (!periodActive) return entries;
    return entries.filter((e) => {
      const day = dayOf(e.date);
      if (!day) return false;
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [entries, dateFrom, dateTo, periodActive]);

  // Per-vehicle filtered entries & totals
  const vehicleFilteredEntries = useMemo(() => {
    if (!selectedVehicle) return null;
    return rangedEntries.filter(e => entryMatchesVehicle(e, selectedVehicle));
  }, [selectedVehicle, rangedEntries]);

  const vehicleTotals = useMemo(() => {
    if (!vehicleFilteredEntries) return null;
    const totalIn = vehicleFilteredEntries.filter(e => e.type === 'in' && isSettled(e)).reduce((s, e) => s + e.amount, 0);
    const totalOut = vehicleFilteredEntries.filter(e => e.type === 'out' && isSettled(e)).reduce((s, e) => s + e.amount, 0);
    return { in: totalIn, out: totalOut, balance: totalIn - totalOut };
  }, [vehicleFilteredEntries]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddTransaction = async () => {
    if (!form.date || !form.category || (!form.in && !form.out)) {
      return alert('Date, Categorie, et au moins un montant (Entree ou Sortie) sont requis.');
    }
    if (form.in && form.out) {
      return alert('Remplir soit Entree soit Sortie, pas les deux.');
    }

    const payload = {
      date: form.date,
      category: form.category,
      vehicleId: form.vehicleId || null,
      amount: parseFloat(form.in || form.out),
      type: form.in ? 'in' : 'out',
      reference: form.reference,
      reason: form.reference || form.category,
      settlement: form.pending ? 'pending' : 'settled'
    };

    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/finance/add`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm({ date: todayStr(), category: '', vehicleId: '', in: '', out: '', reference: '', pending: false });

      // Insert the row the server just returned instead of refetching the whole
      // ledger. Every line typed used to pull back every transaction ever
      // booked — so a morning of data entry got slower with each entry, and
      // nothing appeared in the logs because a slow read is not an error.
      const created = res.data?.transaction;
      if (created) {
        setEntries((prev) => [created, ...prev].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        ));
      } else {
        // Older API shape, or a response we did not recognise: fall back to
        // the full reload rather than leaving the page showing stale totals.
        fetchFinance();
      }
    } catch (err) {
      console.error('Add transaction error:', err);
      alert(err.response?.data?.error || 'Erreur ajout transaction');
    } finally {
      setLoading(false);
    }
  };

  // Open the correction modal, splitting the single stored amount back into
  // the Entrée/Sortie pair the form works in.
  const openEdit = (entry) => {
    setEditTarget(entry);
    setEditError('');
    setEditForm({
      date: entry.date ? new Date(entry.date).toISOString().slice(0, 10) : todayStr(),
      category: entry.category || '',
      vehicleId: entry.vehicleId || '',
      in: entry.type === 'in' ? String(entry.amount ?? '') : '',
      out: entry.type === 'out' ? String(entry.amount ?? '') : '',
      reference: entry.reference || entry.reason || '',
    });
  };

  const saveEdit = async () => {
    if (!editTarget || !editForm) return;
    const { date, category, vehicleId, reference } = editForm;
    if (!date || !category || (!editForm.in && !editForm.out)) {
      return setEditError('Date, catégorie et un montant sont requis.');
    }
    if (editForm.in && editForm.out) {
      return setEditError('Remplir soit Entrée soit Sortie, pas les deux.');
    }
    try {
      setEditSaving(true);
      setEditError('');
      await axios.patch(`${API_URL}/finance/transaction/${editTarget._id}`, {
        date,
        category,
        vehicleId: vehicleId || null,
        type: editForm.in ? 'in' : 'out',
        amount: parseFloat(editForm.in || editForm.out),
        reference,
        // reason is required on the model, so never let it go empty.
        reason: reference || category,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEditTarget(null);
      setEditForm(null);
      fetchFinance();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setEditSaving(false);
    }
  };

  // Open the partial-settle modal for a pending entry (default = full amount).
  const openSettle = (entry) => {
    setSettleTarget(entry);
    setSettleAmount(String(entry.amount || ''));
  };

  // Confirm settlement (full or partial) of the selected pending entry.
  const confirmSettle = async () => {
    if (!settleTarget) return;
    const amt = parseFloat(settleAmount);
    if (!amt || amt <= 0) return alert('Montant invalide.');
    try {
      await axios.patch(`${API_URL}/finance/transaction/${settleTarget._id}/settle`, { amount: amt }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettleTarget(null);
      setSettleAmount('');
      fetchFinance();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur règlement');
    }
  };

  // Lump payment to the selected supplier (applied oldest-first server-side).
  const handlePaySupplier = async () => {
    const amt = parseFloat(supplierPayAmount);
    if (!selectedSupplier) return;
    if (!amt || amt <= 0) return alert('Montant invalide.');
    try {
      const res = await axios.post(`${API_URL}/finance/supplier/pay`,
        { category: selectedSupplier, amount: amt },
        { headers: { Authorization: `Bearer ${token}` } });
      setSupplierPayOpen(false);
      setSupplierPayAmount('');
      if (res.data?.unapplied > 0) {
        alert(`Payé ${res.data.applied}. ${res.data.unapplied} dépassait le montant dû et n'a pas été appliqué.`);
      }
      fetchFinance();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur paiement fournisseur');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await axios.post(`${API_URL}/finance/categories`,
        { name: newCategory.trim(), isSupplier: newCategoryIsSupplier },
        { headers: { Authorization: `Bearer ${token}` } });
      setNewCategory('');
      setNewCategoryIsSupplier(false);
      setShowAddCategory(false);
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur ajout categorie');
    }
  };

  // Supplier categories + the selected supplier's running balance.
  const supplierCategories = useMemo(
    () => categories.filter(c => c.isSupplier).map(c => c.name),
    [categories]
  );
  // Deliberately reads `entries`, not `rangedEntries`: what you owe a supplier
  // is a running balance carried across periods, not a figure for one month.
  // Scoping it to August would show a debt of zero the moment the purchase
  // that created it fell outside the window.
  const supplierTotals = useMemo(() => {
    if (!selectedSupplier) return null;
    const rows = entries.filter(e => e.type === 'out' && e.category === selectedSupplier);
    // Settled rows = amounts already paid. Pending rows hold the OUTSTANDING
    // (remaining) amount. So total purchased = paid + still owed.
    const paye = rows.filter(isSettled).reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const reste = rows.filter(isPending).reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return { achete: paye + reste, paye, reste };
  }, [selectedSupplier, entries]);

  const getPlateNumber = (id) => {
    const v = vehicles.find(v => v._id === id);
    return v ? v.plateNumber : '';
  };

  const formatFCFA = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount || 0);
  };

  const getWorkshopName = () =>
    user?.businessName || user?.details?.businessName || "Atelier";

  // Exports exactly what is on screen — period, vehicle, supplier, category tab
  // and text search all already applied. An export that quietly returned
  // everything would contradict the totals shown right above it, and the
  // person reconciling a month would not notice until the numbers disagreed.
  const handleExportCSV = () => {
    downloadLedgerCSV(filteredEntries, {
      from: dateFrom,
      to: dateTo,
      plateOf: getPlateNumber,
    });
  };

  // Filter entries based on active tab + vehicle filter + selected supplier
  let baseEntries = selectedVehicle
    ? rangedEntries.filter(e => entryMatchesVehicle(e, selectedVehicle))
    : rangedEntries;
  if (selectedSupplier) {
    baseEntries = baseEntries.filter(e => e.category === selectedSupplier);
  }
  const catEntries = activeFilter === 'all'
    ? baseEntries
    : baseEntries.filter(e => e.category?.toLowerCase().includes(activeFilter));
  // Free-text search over reference / note / category / attached plate.
  // Matches plain text AND normalized plates (so "AA-120-EH-01" finds "aa120eh01").
  const tq = textQuery.trim();
  const tqNorm = normPlate(tq);
  const filteredEntries = !tq ? catEntries : catEntries.filter(e => {
    const hay = `${e.reference || ''} ${e.reason || ''} ${e.category || ''} ${getPlateNumber(e.vehicleId) || ''}`;
    return hay.toLowerCase().includes(tq.toLowerCase()) || (tqNorm && normPlate(hay).includes(tqNorm));
  });

  // Calculate totals for filtered view (settled cash only)
  const filteredIn = filteredEntries.filter(e => e.type === 'in' && isSettled(e)).reduce((s, e) => s + e.amount, 0);
  const filteredOut = filteredEntries.filter(e => e.type === 'out' && isSettled(e)).reduce((s, e) => s + e.amount, 0);
  const filteredBalance = filteredIn - filteredOut;

  // Period totals — cash counts SETTLED entries only.
  //
  // With no period set these are the all-time figures and "Solde caisse" is
  // the real till balance. Once a period is set they describe that period
  // only, and the balance becomes net movement over it — not what is in the
  // till. The label changes to match; see the summary bar below.
  const globalIn = rangedEntries.filter(e => e.type === 'in' && isSettled(e)).reduce((s, e) => s + e.amount, 0);
  const globalOut = rangedEntries.filter(e => e.type === 'out' && isSettled(e)).reduce((s, e) => s + e.amount, 0);
  const globalBalance = globalIn - globalOut;

  // Receivables (pending 'in') and payables (pending 'out') — kept out of the balance.
  const receivableTotal = rangedEntries.filter(e => e.type === 'in' && isPending(e)).reduce((s, e) => s + e.amount, 0);
  const payableTotal = rangedEntries.filter(e => e.type === 'out' && isPending(e)).reduce((s, e) => s + e.amount, 0);

  // Category breakdown for summary
  const categoryTotals = {};
  rangedEntries.forEach(e => {
    const cat = e.category || 'Divers';
    if (!categoryTotals[cat]) categoryTotals[cat] = { in: 0, out: 0 };
    if (e.type === 'in') categoryTotals[cat].in += e.amount;
    else categoryTotals[cat].out += e.amount;
  });

  // Filter tabs
  const filterTabs = [
    { id: 'all', label: 'Tout' },
    { id: 'client', label: 'Clients' },
    { id: 'fournisseur', label: 'Fournisseurs' },
    { id: 'assurance', label: 'Assurance' },
  ];

  return (
    <Page
      title="Caisse"
      subtitle={getWorkshopName()}
      actions={
        <Button
          onClick={handleExportCSV}
          disabled={filteredEntries.length === 0}
          title="Exporter les écritures affichées en CSV (Excel)"
        >
          Exporter CSV
          {filteredEntries.length > 0 && (
            <span className="text-[#6E665C]"> · {filteredEntries.length}</span>
          )}
        </Button>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}

      {/* Accounting period — scopes every figure on the page below it. */}
      <Panel title="Période" bodyClass="px-[18px] py-3.5">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2.5">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[.16em] text-[#6E665C]">Du</span>
            <input
              type="date" value={dateFrom} max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className={`${cell} w-[150px]`}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[.16em] text-[#6E665C]">Au</span>
            <input
              type="date" value={dateTo} min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className={`${cell} w-[150px]`}
            />
          </label>

          <div className="flex flex-wrap gap-[5px] items-center">
            {[
              { label: `${monthLabel(0)}`, range: monthRange(0) },
              { label: `${monthLabel(1)}`, range: monthRange(1) },
            ].map(({ label, range }) => {
              const on = dateFrom === range.from && dateTo === range.to;
              return (
                <button
                  key={label}
                  onClick={() => { setDateFrom(range.from); setDateTo(range.to); }}
                  className={`h-[30px] px-2.5 rounded-[7px] text-[11px] font-medium capitalize transition-colors ${
                    on ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
                  }`}
                >
                  {label}
                </button>
              );
            })}
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              disabled={!periodActive}
              className={`h-[30px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                !periodActive ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
              }`}
            >
              Tout
            </button>
          </div>

          {periodActive && (
            <p className="text-[11px] text-[#8F877B] ml-auto font-['Space_Grotesk',sans-serif] tabular-nums">
              {rangedEntries.length} écriture{rangedEntries.length > 1 ? 's' : ''}
              {dateFrom && ` · du ${new Date(dateFrom).toLocaleDateString('fr-FR')}`}
              {dateTo && ` au ${new Date(dateTo).toLocaleDateString('fr-FR')}`}
            </p>
          )}
        </div>
      </Panel>

      {/* Global summary — one hairline-divided bar, not six coloured cards. */}
      <Panel bodyClass="">
        <div className="flex flex-wrap divide-x divide-y sm:divide-y-0 divide-[#241F1B]">
          <Stat label="Total entrées" tone={signal.ok}>
            <Money value={globalIn} />
          </Stat>
          <Stat label="Total sorties" tone={signal.danger}>
            <Money value={globalOut} />
          </Stat>
          <Stat
            label={periodActive ? 'Solde période' : 'Solde caisse'}
            tone={globalBalance >= 0 ? '#F4F1EA' : signal.danger}
            title={periodActive
              ? 'Entrées moins sorties sur la période — pas le solde réel de la caisse'
              : 'Solde réel de la caisse'}
          >
            <Money value={globalBalance} />
          </Stat>
          <Stat label="À recevoir" tone={signal.warn} title="Argent qui vous est dû, pas encore encaissé">
            <Money value={receivableTotal} />
          </Stat>
          <Stat label="À payer" tone={signal.warn} title="Argent que vous devez, pas encore payé">
            <Money value={payableTotal} />
          </Stat>
          <Stat label="Écritures">{rangedEntries.length}</Stat>
        </div>
      </Panel>

      {/* Totals per group. Answers "how much did I pay in wages in August" when
          wages are recorded as one category per employee — the categories hold
          the detail, the group holds the total. */}
      <Panel
        title="Totaux par groupe"
        action={
          <a href="/workshop/categories" className="text-[11.5px] text-[#8F877B] hover:text-[#F4F1EA] transition-colors">
            Gérer les groupes
          </a>
        }
        bodyClass=""
      >
        {groupsLoading ? (
          <p className="py-10 text-center text-[12.5px] text-[#6E665C]">Calcul des totaux…</p>
        ) : groupRows.length === 0 ? (
          <div className="py-10 px-[18px] text-center">
            <p className="text-[13px] text-[#8F877B]">Aucun total à afficher</p>
            <p className="text-[11.5px] text-[#5C554C] mt-1.5 max-w-[420px] mx-auto">
              Regroupez vos catégories — une par employé, toutes dans « Salaires » —
              pour obtenir le total versé chaque mois.
            </p>
          </div>
        ) : (
          <div>
            {groupRows.map((g) => {
              const open = openGroup === g.group;
              return (
                <div key={g.group || '__none__'} className="border-b border-[#1E1B18] last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setOpenGroup(open ? null : g.group)}
                    className="w-full flex items-center gap-3 px-[18px] py-3 text-left hover:bg-[#1A1613] transition-colors"
                  >
                    <span className={`text-[10px] text-[#4E4740] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
                    <span className="text-[13px] text-[#F4F1EA] flex-1 truncate">
                      {g.group || <span className="text-[#8F877B] italic">Sans groupe</span>}
                    </span>
                    <span className="text-[10.5px] text-[#5C554C] tabular-nums whitespace-nowrap">
                      {g.monthly.length} mois · {g.count} écr.
                    </span>
                    <span className="w-[130px] text-right shrink-0">
                      <Money value={g.out} fit={false} className="text-[#E2574B]" />
                    </span>
                  </button>

                  {open && (
                    <div className="px-[18px] pb-3.5 pt-1 bg-[#131110]">
                      {g.monthly.map((m) => (
                        <div key={m.month} className="mb-3 last:mb-0">
                          <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-[#241F1B]">
                            <span className="text-[12px] text-[#C9C3B6] tabular-nums">
                              {monthKeyLabel(m.month)}
                            </span>
                            <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[13px] text-[#F4F1EA]">
                              <Money value={m.out} fit={false} />
                            </span>
                          </div>
                          {/* The per-employee detail the categories were created
                              for — kept, not replaced by the total. */}
                          {m.categories.map((c) => (
                            <div key={c.category}
                              className="flex items-baseline justify-between gap-4 py-1 pl-3">
                              <span className="text-[11.5px] text-[#8F877B] truncate">{c.category}</span>
                              <span className="text-[11.5px] text-[#8F877B] tabular-nums whitespace-nowrap">
                                <Money value={c.out} fit={false} />
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* Vehicle filter */}
      <Panel title="Filtrer par véhicule">
        <div ref={vehicleSearchRef} className="relative">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={vehicleSearch}
                onChange={(e) => { setVehicleSearch(e.target.value); setVehicleDropdownOpen(true); }}
                onFocus={() => setVehicleDropdownOpen(true)}
                placeholder="Plaque, marque, modèle ou client…"
                className={inputClass}
              />
              {/* Dropdown */}
              {vehicleDropdownOpen && vehicleSearch.trim() && (
                <div className="absolute z-50 mt-1 w-full rounded-xl border border-[#2B2724] bg-[#141210] shadow-2xl overflow-hidden">
                  {vehicleSearchResults.length === 0 ? (
                    <div className="px-3 py-3 text-[12px] text-[#6E665C] text-center">Aucun véhicule trouvé</div>
                  ) : (
                    <ul className="max-h-52 overflow-y-auto">
                      {vehicleSearchResults.map(v => (
                        <li
                          key={v._id}
                          onClick={() => {
                            setSelectedVehicle(v);
                            setVehicleSearch('');
                            setVehicleDropdownOpen(false);
                          }}
                          className="px-3 py-2 cursor-pointer hover:bg-[#1A1613] transition-colors border-b border-[#1E1B18] last:border-0"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.02em] text-[#F4F1EA]">
                              {v.plateNumber}
                            </span>
                            <span className="text-[11px] text-[#8F877B]">{v.brand} {v.model}</span>
                          </div>
                          {v.owner?.name && (
                            <div className="text-[10.5px] text-[#5C554C] mt-0.5">{v.owner.name}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            {selectedVehicle && (
              <Button className="h-[38px]" onClick={() => setSelectedVehicle(null)}>Effacer</Button>
            )}
          </div>

          {/* Selected vehicle badge + totals */}
          {selectedVehicle && vehicleTotals && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="font-['Space_Grotesk',sans-serif] text-[13px] tracking-[.02em] text-[#F4F1EA]">
                  {selectedVehicle.plateNumber}
                </span>
                <span className="text-[12px] text-[#8F877B]">
                  {selectedVehicle.brand} {selectedVehicle.model}
                  {selectedVehicle.owner?.name ? ` — ${selectedVehicle.owner.name}` : ''}
                </span>
              </div>
              <div className="rounded-xl border border-[#241F1B] flex flex-wrap divide-x divide-[#241F1B]">
                <Stat label="Revenus" tone={signal.ok}><Money value={vehicleTotals.in} /></Stat>
                <Stat label="Dépenses" tone={signal.danger}><Money value={vehicleTotals.out} /></Stat>
                <Stat label="Résultat net" tone={vehicleTotals.balance >= 0 ? '#F4F1EA' : signal.danger}>
                  <Money value={vehicleTotals.balance} />
                </Stat>
              </div>
            </div>
          )}
        </div>
      </Panel>

      {/* Comptes fournisseurs */}
      {supplierCategories.length > 0 && (
        <Panel title="Compte fournisseur">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className={`${selectClass} max-w-[280px]`}
            >
              <option value="">Sélectionner un fournisseur…</option>
              {supplierCategories.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
            {selectedSupplier && (
              <Button className="h-[38px]" onClick={() => setSelectedSupplier('')}>Effacer</Button>
            )}
          </div>

          {selectedSupplier && supplierTotals && (
            <div className="mt-4 flex flex-col sm:flex-row gap-3 items-stretch">
              <div className="flex-1 rounded-xl border border-[#241F1B] flex flex-wrap divide-x divide-[#241F1B]">
                <Stat label="Total acheté"><Money value={supplierTotals.achete} /></Stat>
                <Stat label="Total réglé" tone={signal.ok}><Money value={supplierTotals.paye} /></Stat>
                <Stat label="Reste à payer" tone={supplierTotals.reste > 0 ? signal.danger : signal.ok}>
                  <Money value={supplierTotals.reste} />
                </Stat>
              </div>
              <div className="flex items-center">
                <Button
                  variant="primary"
                  className="w-full sm:w-auto"
                  onClick={() => { setSupplierPayAmount(String(supplierTotals.reste || '')); setSupplierPayOpen(true); }}
                  disabled={supplierTotals.reste <= 0}
                >
                  Régler le fournisseur
                </Button>
              </div>
            </div>
          )}
        </Panel>
      )}

      {/* Category breakdown */}
      {Object.keys(categoryTotals).length > 0 && (
        <Panel title="Répartition par catégorie">
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
            {Object.entries(categoryTotals).map(([cat, totals]) => (
              <div key={cat}
                   className="flex items-center justify-between gap-3 rounded-[9px] border border-[#241F1B] bg-[#141210] px-3.5 py-2.5">
                <span className="text-[12.5px] text-[#C9C3B6] min-w-0 truncate">{cat}</span>
                <div className="flex gap-3 text-[11.5px] whitespace-nowrap font-['Space_Grotesk',sans-serif] tabular-nums">
                  {totals.in > 0 && <Money value={totals.in} sign="+" fit={false} className="text-[#74A98D]" />}
                  {totals.out > 0 && <Money value={totals.out} sign="-" fit={false} className="text-[#E2574B]" />}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Ledger */}
      <Panel
        title="Journal"
        bodyClass=""
        action={
          <div className="flex gap-[5px]">
            {filterTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                  activeFilter === tab.id ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
      >
        {/* Free-text search over reference/note (e.g. a plate typed in the note) */}
        <div className="px-[18px] py-3 border-b border-[#241F1B] flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <input
              type="text"
              value={textQuery}
              onChange={(e) => setTextQuery(e.target.value)}
              placeholder="Rechercher dans réf./note (plaque, mot-clé…)"
              className={`${inputClass} h-[32px] pr-8 text-[12px]`}
            />
            {textQuery && (
              <button onClick={() => setTextQuery('')} title="Effacer"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6E665C] hover:text-[#F4F1EA] text-[12px]">✕</button>
            )}
          </div>

          {(activeFilter !== 'all' || tq) && (
            <div className="text-[11px] text-[#6E665C] whitespace-nowrap font-['Space_Grotesk',sans-serif] tabular-nums">
              {tq && <span className="text-[#8F877B]">{filteredEntries.length} résultat{filteredEntries.length > 1 ? 's' : ''} · </span>}
              Entrées <span className="text-[#74A98D]">{formatFCFA(filteredIn)}</span>
              {' · '}Sorties <span className="text-[#E2574B]">{formatFCFA(filteredOut)}</span>
              {' · '}Net <span style={{ color: filteredBalance >= 0 ? '#F4F1EA' : signal.danger }}>{formatFCFA(filteredBalance)}</span>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse" style={{ minWidth: 960 }}>
            <thead>
              <tr className="border-b border-[#241F1B]">
                {['Date', 'Catégorie', 'Véhicule', 'Entrée', 'Sortie', 'Référence / note', 'Statut', ''].map((h, i) => (
                  <th key={i}
                      className="px-3 py-3 text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] font-normal whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Inline add row */}
              <tr className="border-b border-[#241F1B] bg-[#141210]">
                <td className="px-3 py-2 w-[140px]">
                  <input type="date" name="date" value={form.date} onChange={handleChange} className={cell} />
                </td>
                <td className="px-3 py-2 w-[190px]">
                  <div className="flex gap-1 items-center">
                    <select name="category" value={form.category} onChange={handleChange} className={cellSelect}>
                      <option value="">Catégorie…</option>
                      {categories.map(c => (
                        <option key={c._id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowAddCategory(true)}
                      className="shrink-0 w-[26px] h-[26px] rounded-[7px] border border-[#2B2724] text-[#8F877B]
                                 hover:text-[#F4F1EA] hover:border-[#4E4740] transition-colors"
                      title="Ajouter une catégorie"
                    >+</button>
                  </div>
                </td>
                <td className="px-3 py-2 w-[130px]">
                  <select name="vehicleId" value={form.vehicleId} onChange={handleChange} className={cellSelect}>
                    <option value="">—</option>
                    {vehicles.map(v => (
                      <option key={v._id} value={v._id}>{v.plateNumber}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 w-[120px]">
                  <input type="number" name="in" placeholder="0" value={form.in} onChange={handleChange}
                         className={`${cell} text-right`} />
                </td>
                <td className="px-3 py-2 w-[120px]">
                  <input type="number" name="out" placeholder="0" value={form.out} onChange={handleChange}
                         className={`${cell} text-right`} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <input type="text" name="reference" placeholder="Réf. / note" value={form.reference}
                           onChange={handleChange} className={cell} />
                    <Button
                      variant="primary"
                      className="h-[30px] px-3 text-[11.5px]"
                      onClick={handleAddTransaction}
                      disabled={loading || !form.date || !form.category || (!form.in && !form.out)}
                    >
                      {loading ? '…' : 'Ajouter'}
                    </Button>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Checkbox
                    label="À crédit"
                    checked={form.pending}
                    onChange={(e) => setForm(prev => ({ ...prev, pending: e.target.checked }))}
                    className="text-[11.5px]"
                  />
                </td>
                <td className="px-3 py-2" />
              </tr>

              {/* Data rows */}
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-[12.5px] text-[#6E665C]">
                    {activeFilter === 'all' ? 'Aucune écriture enregistrée' : `Aucune écriture « ${activeFilter} »`}
                  </td>
                </tr>
              ) : (
                filteredEntries.map(entry => {
                  const pending = isPending(entry);
                  return (
                    <tr key={entry._id}
                        className="border-b border-[#1E1B18] last:border-0 hover:bg-[#1A1613] transition-colors">
                      <td className="px-3 py-2.5 text-[12.5px] text-[#C9C3B6] whitespace-nowrap">
                        {new Date(entry.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="px-2 py-[2px] rounded-full text-[11px] bg-[#1E1B17] text-[#C9C3B6]">
                          {entry.category}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-[11.5px] text-[#8F877B] font-['Space_Grotesk',sans-serif]">
                        {getPlateNumber(entry.vehicleId)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-['Space_Grotesk',sans-serif] tabular-nums text-[12.5px]"
                          style={{ color: pending ? 'rgba(223,162,60,.7)' : signal.ok }}>
                        {entry.type === 'in' ? <Money value={entry.amount} fit={false} /> : ''}
                      </td>
                      <td className="px-3 py-2.5 text-right font-['Space_Grotesk',sans-serif] tabular-nums text-[12.5px]"
                          style={{ color: pending ? 'rgba(223,162,60,.7)' : signal.danger }}>
                        {entry.type === 'out' ? <Money value={entry.amount} fit={false} /> : ''}
                      </td>
                      <td className="px-3 py-2.5 text-[11.5px] text-[#8F877B]">
                        {entry.reference || entry.reason}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {pending ? (
                          <div className="flex items-center gap-2">
                            <Badge tone="warn">{entry.type === 'in' ? 'À recevoir' : 'À payer'}</Badge>
                            <Button
                              className="h-[24px] px-2 text-[10.5px]"
                              onClick={() => openSettle(entry)}
                              title="Marquer comme réglé (l'argent a bougé)"
                            >
                              Régler
                            </Button>
                          </div>
                        ) : (
                          <Badge tone="ok">Réglé</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Only offered on rows that actually have a history,
                              so its presence is itself the signal that this
                              line is not what it was originally written as. */}
                          {wasModified(entry) && (
                            <button
                              onClick={() => setHistoryTarget(entry)}
                              title="Voir l'historique des modifications"
                              className="h-[24px] px-2 rounded-[7px] border border-[#3A332C]
                                         text-[10.5px] transition-colors hover:border-[#5C554C]"
                              style={{ color: signal.warn }}
                            >
                              modifié
                            </button>
                          )}
                          <Button
                            className="h-[24px] px-2 text-[10.5px]"
                            onClick={() => openEdit(entry)}
                            title="Corriger cette écriture"
                          >
                            Modifier
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <TransactionHistoryModal
        open={!!historyTarget}
        entry={historyTarget}
        token={token}
        onClose={() => setHistoryTarget(null)}
      />

      {/* Add category */}
      <Modal
        open={showAddCategory}
        onClose={() => { setShowAddCategory(false); setNewCategory(''); setNewCategoryIsSupplier(false); }}
        subtitle="Caisse"
        title="Nouvelle catégorie"
        footer={
          <>
            <Button onClick={() => { setShowAddCategory(false); setNewCategory(''); setNewCategoryIsSupplier(false); }}>
              Annuler
            </Button>
            <Button variant="primary" onClick={handleAddCategory} disabled={!newCategory.trim()}>
              Créer
            </Button>
          </>
        }
      >
        <Field label="Nom de la catégorie" hint="Ex : client, fournisseur, assurance, loyer, salaire, carburant…">
          <input
            type="text"
            placeholder="Nom de la catégorie"
            value={newCategory}
            onChange={e => setNewCategory(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
            className={inputClass}
            autoFocus
          />
        </Field>
        <Checkbox
          label="C'est un fournisseur"
          hint="Crée un compte avec un solde dû, réglable en une fois."
          checked={newCategoryIsSupplier}
          onChange={e => setNewCategoryIsSupplier(e.target.checked)}
        />
      </Modal>

      {/* Correct an entry */}
      <Modal
        open={!!editTarget && !!editForm}
        onClose={() => { setEditTarget(null); setEditForm(null); }}
        subtitle={editTarget?.category}
        title="Modifier l'écriture"
        footer={
          <>
            <Button onClick={() => { setEditTarget(null); setEditForm(null); }}>Annuler</Button>
            <Button variant="primary" onClick={saveEdit} disabled={editSaving}>
              {editSaving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </>
        }
      >
        {editError && <ErrorNote>{editError}</ErrorNote>}
        {editForm && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Date">
                <input
                  type="date" value={editForm.date} className={inputClass}
                  onChange={(e) => setEditForm(f => ({ ...f, date: e.target.value }))}
                />
              </Field>
              <Field label="Catégorie">
                <select
                  value={editForm.category} className={selectClass}
                  onChange={(e) => setEditForm(f => ({ ...f, category: e.target.value }))}
                >
                  <option value="">Catégorie…</option>
                  {categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Entrée" hint="Laisser vide si c'est une sortie">
                <input
                  type="number" value={editForm.in} className={inputClass}
                  onChange={(e) => setEditForm(f => ({ ...f, in: e.target.value, out: '' }))}
                />
              </Field>
              <Field label="Sortie" hint="Laisser vide si c'est une entrée">
                <input
                  type="number" value={editForm.out} className={inputClass}
                  onChange={(e) => setEditForm(f => ({ ...f, out: e.target.value, in: '' }))}
                />
              </Field>
              <Field label="Véhicule">
                <select
                  value={editForm.vehicleId || ''} className={selectClass}
                  onChange={(e) => setEditForm(f => ({ ...f, vehicleId: e.target.value }))}
                >
                  <option value="">—</option>
                  {vehicles.map(v => <option key={v._id} value={v._id}>{v.plateNumber}</option>)}
                </select>
              </Field>
              <Field label="Référence / note">
                <input
                  type="text" value={editForm.reference} className={inputClass}
                  onChange={(e) => setEditForm(f => ({ ...f, reference: e.target.value }))}
                />
              </Field>
            </div>
            <Note tone="warn">
              Le statut (réglé / à crédit) ne se change pas ici — utilisez « Régler »,
              qui enregistre aussi le mouvement d'argent.
            </Note>
          </>
        )}
      </Modal>

      {/* Partial settle */}
      <Modal
        open={!!settleTarget}
        onClose={() => { setSettleTarget(null); setSettleAmount(''); }}
        subtitle={settleTarget?.category}
        title={`Régler ${settleTarget?.type === 'in' ? 'la créance' : 'la dette'}`}
        footer={
          <>
            <Button onClick={() => { setSettleTarget(null); setSettleAmount(''); }}>Annuler</Button>
            <Button variant="primary" onClick={confirmSettle}>Régler</Button>
          </>
        }
      >
        <p className="text-[12.5px] text-[#8F877B]">
          Reste dû :{' '}
          <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
            {formatFCFA(settleTarget?.amount)}
          </span>
        </p>
        <Field
          label="Montant à régler maintenant"
          hint="Réglez le total pour solder la ligne, ou un montant partiel — le reste restera dû."
        >
          <input
            type="number" value={settleAmount} onChange={e => setSettleAmount(e.target.value)} autoFocus
            max={settleTarget?.amount}
            className={inputClass}
          />
        </Field>
      </Modal>

      {/* Supplier lump payment */}
      <Modal
        open={supplierPayOpen && !!supplierTotals}
        onClose={() => { setSupplierPayOpen(false); setSupplierPayAmount(''); }}
        subtitle="Compte fournisseur"
        title={`Régler ${selectedSupplier}`}
        footer={
          <>
            <Button onClick={() => { setSupplierPayOpen(false); setSupplierPayAmount(''); }}>Annuler</Button>
            <Button variant="primary" onClick={handlePaySupplier}>Payer</Button>
          </>
        }
      >
        <p className="text-[12.5px] text-[#8F877B]">
          Reste à payer :{' '}
          <span className="font-['Space_Grotesk',sans-serif] tabular-nums text-[#F4F1EA]">
            {formatFCFA(supplierTotals?.reste)}
          </span>
        </p>
        <Field label="Montant du paiement" hint="Appliqué aux achats les plus anciens en premier.">
          <input
            type="number" value={supplierPayAmount} onChange={e => setSupplierPayAmount(e.target.value)} autoFocus
            max={supplierTotals?.reste}
            className={inputClass}
          />
        </Field>
      </Modal>
    </Page>
  );
};

export default CashbookLedger;
